import { DEFAULT_RTM_UNIT_COUNT } from '@/consts/RTM';
import { useFeatureFlagEnabled } from '@/hooks/featureFlags/useFeatureFlagEnabled';
import { type IDefaultPatientColumns } from '@/Pages/store';
import { type MainIndication } from '@/services/patientsService/types';
import { usePermissionsService } from '@/services/permissionService';

import useCurrentClinic, { clinicAllowedActions } from '../useCurrentClinic';

export interface IFeatureMapSetting {
  read: boolean;
  write: boolean;
}

export interface IFeatureMap {
  exerciseLibrary: IFeatureMapSetting;
  templates: IFeatureMapSetting;
  feedbacks: IFeatureMapSetting;
  clinicManagement: boolean;
  rtm: IFeatureMapSetting;
  communication: boolean;
  homeExercisePlan: boolean;
  defaultPatientColumns?: IDefaultPatientColumns;
  clinicianApp: boolean;
  patientAppEnabled: boolean;
  fallPreventionProgram: boolean;
  careEngineEnabled: boolean;
  pointclick?: boolean;
  clinicManagementView: boolean;
  displayDeviceAdjustment: boolean;
  epicEmr: boolean;
  yardiEmr: boolean;
  enableDailyStepGoal: boolean;
  cptCode8081MaxUnits: number;
  enableExternalEmr: boolean;
  enableMrn: boolean;
  enablePatientOverviewV2?: boolean;
  careRecommendations?: boolean;
  careRecommendationsAiSummarization?: boolean;
  enableRTMTab?: boolean;
  copyToEmrLlmEnabled?: boolean;
  enableFallriskPassiveIndicators?: boolean;
  disableFallRiskNorms?: boolean;
  experimentalSummaryEnabled?: boolean;
  reportsEnabled: boolean;
  adminDashboardReportsTabsEnabled: boolean;
  enableMultiIndicationDemo: boolean;
  noEpisodeOfCare: boolean;
  editableSystemRosterCards: boolean;
  cohortAgentAuthoring: boolean;
  fallRiskTrendGapLine: boolean;
  eventBasedAttributes: boolean;
  usePatientEncountersActiveView: boolean;
  indicationPatientOverview: boolean;
  workspacePickerTwoPane: boolean;
  eventBasedAttrsBulkAssign: boolean;
  computedColumns: boolean;
  newPatientPrimaryProgressIndication: boolean;
  mainIndication: MainIndication | null;
}

export default function useFeatureMap() {
  const { clinic } = useCurrentClinic();
  const { isAdmin, isActionAllowed } = usePermissionsService();
  const isIndicationPatientOverviewEnabled = useFeatureFlagEnabled('indication-patient-overview');
  const isBulkAssignEnabled = useFeatureFlagEnabled('event-based-attrs-bulk-assign');
  const isWorkspacePickerTwoPaneEnabled = useFeatureFlagEnabled('portal-workspace-picker-two-pane');
  const allowReadContent = clinic?.featureMap?.homeExercisePlanEnabled ?? false;
  const allowEditContent =
    (allowReadContent && isActionAllowed(clinicAllowedActions.contentEditor)) ?? false;

  const featureMap: IFeatureMap = {
    rtm: {
      read: clinic?.featureMap.rtmEnabled ?? false,
      write: clinic?.featureMap.rtmEnabled ?? false,
    },
    clinicManagement: isActionAllowed(clinicAllowedActions.write),
    templates: {
      read: allowReadContent,
      write: allowEditContent,
    },
    exerciseLibrary: {
      read: allowReadContent,
      write: allowEditContent,
    },
    feedbacks: {
      read: clinic?.featureMap?.homeExercisePlanEnabled ?? false,
      write: clinic?.featureMap?.homeExercisePlanEnabled ?? false,
    },
    communication: !!clinic?.featureMap.communicationEnabled,
    homeExercisePlan: !!clinic?.featureMap?.homeExercisePlanEnabled,

    clinicianApp: !!clinic?.featureMap.clinicianAppEnabled,
    patientAppEnabled: !!clinic?.featureMap.patientAppEnabled,

    fallPreventionProgram: !!clinic?.featureMap?.fallPreventionProgram,

    careEngineEnabled: !!clinic?.featureMap.careEngineEnabled,

    pointclick: !!clinic?.featureMap?.pointclick,
    clinicManagementView: isAdmin() || isActionAllowed(clinicAllowedActions.viewAuditorTool),
    displayDeviceAdjustment: !!clinic?.featureMap.displayDeviceAdjustment,
    epicEmr: !!clinic?.featureMap.epicEmr,
    yardiEmr: !!clinic?.featureMap.yardiEmr,
    enableDailyStepGoal: !!clinic?.featureMap.enableDailyStepGoal,
    cptCode8081MaxUnits: clinic?.featureMap.cptCode8081MaxUnits ?? DEFAULT_RTM_UNIT_COUNT,
    enableExternalEmr: !!clinic?.featureMap.enableExternalEmr,
    enableMrn: !!clinic?.featureMap.enableMrn,
    careRecommendations: Boolean(clinic?.featureMap.careRecommendations),
    careRecommendationsAiSummarization: Boolean(
      clinic?.featureMap.careRecommendationsAiSummarization,
    ),
    enablePatientOverviewV2: Boolean(clinic?.featureMap.patientOverviewFallRiskV2),
    enableRTMTab: !!(clinic?.featureMap?.patientOverviewRtmTab && clinic?.featureMap.rtmEnabled),
    copyToEmrLlmEnabled: Boolean(clinic?.featureMap.copyForEmrLlmEnabled),
    enableFallriskPassiveIndicators: Boolean(clinic?.featureMap.enableFallriskPassiveIndicators),
    disableFallRiskNorms: Boolean(clinic?.featureMap.disableFallRiskNorms),
    experimentalSummaryEnabled: Boolean(clinic?.featureMap.experimentalSummaryEnabled),
    reportsEnabled: Boolean(clinic?.featureMap.reportsEnabled),
    adminDashboardReportsTabsEnabled: Boolean(clinic?.featureMap.reportsDesignerAdminDashboardTabs),
    enableMultiIndicationDemo: Boolean(clinic?.featureMap.multiIndicationDemoEnabled),
    noEpisodeOfCare: Boolean(clinic?.featureMap.noEpisodeOfCare),
    editableSystemRosterCards: Boolean(clinic?.featureMap.editableSystemRosterCards),
    cohortAgentAuthoring: Boolean(clinic?.featureMap.cohortAgentAuthoring),
    fallRiskTrendGapLine: Boolean(clinic?.featureMap.fallRiskTrendGapLine),
    eventBasedAttributes: Boolean(clinic?.featureMap.eventBasedAttributes),
    usePatientEncountersActiveView: Boolean(clinic?.featureMap.usePatientEncountersActiveView),
    newPatientPrimaryProgressIndication: Boolean(
      clinic?.featureMap.newPatientPrimaryProgressIndication,
    ),
    mainIndication: clinic?.featureMap.mainIndication ?? null,
    indicationPatientOverview: isIndicationPatientOverviewEnabled,
    eventBasedAttrsBulkAssign: isBulkAssignEnabled,
    workspacePickerTwoPane: isWorkspacePickerTwoPaneEnabled,
    computedColumns: Boolean(clinic?.featureMap.computedColumns),

    /**

     of the isLocal from the useEnvironment hook
     * featureUnderConstruction: isLocal || 'off',
     */
  };

  return featureMap;
}
